import { updatePositions } from 'components/form_builder/features/components/componentSlice'
import { useCallback } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

export const ALIGNMENT = {
  LEFT: 'left',
  RIGHT: 'right',
  TOP: 'top',
  BOTTOM: 'bottom',
  CENTER_HORIZONTAL: 'center-horizontal',
  CENTER_VERTICAL: 'center-vertical',
}

export default function useAlignment() {
  const dispatch = useDispatch()
  const allComponents = useSelector(({ components }) => components.all || {}, shallowEqual)
  const selectedIds = useSelector(({ components }) => components.selected || [], shallowEqual)

  const onAlign = useCallback(
    (alignment) => {
      // No need to align if less than two components are selected.
      if (selectedIds.length < 2) return

      const selectedObjects = selectedIds.map((id) => allComponents[id])

      const bounds = selectedObjects.reduce(
        (acc, { rect }) => ({
          minX: Math.min(acc.minX, rect.x),
          maxX: Math.max(acc.maxX, rect.x + rect.width),
          minY: Math.min(acc.minY, rect.y),
          maxY: Math.max(acc.maxY, rect.y + rect.height),
        }),
        { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity }
      )

      const widestComponent = selectedObjects.reduce((a, b) => (a.rect.width > b.rect.width ? a : b))
      const centerX = widestComponent.rect.x + widestComponent.rect.width / 2

      const tallestComponent = selectedObjects.reduce((a, b) => (a.rect.height > b.rect.height ? a : b))
      const centerY = tallestComponent.rect.y + tallestComponent.rect.height / 2

      const updatedComponents = selectedObjects.map((component) => {
        const { rect } = component
        let newPosition = {}

        switch (alignment) {
          case ALIGNMENT.LEFT:
            newPosition = { x: bounds.minX }
            break
          case ALIGNMENT.RIGHT:
            newPosition = { x: bounds.maxX - rect.width }
            break
          case ALIGNMENT.TOP:
            newPosition = { y: bounds.minY }
            break
          case ALIGNMENT.BOTTOM:
            newPosition = { y: bounds.maxY - rect.height }
            break
          case ALIGNMENT.CENTER_VERTICAL:
            newPosition = { x: centerX - rect.width / 2 }
            break
          case ALIGNMENT.CENTER_HORIZONTAL:
            newPosition = { y: centerY - rect.height / 2 }
            break
          default:
            return component
        }

        return {
          ...component,
          rect: {
            ...rect,
            ...newPosition,
          },
        }
      })

      dispatch(updatePositions({ components: updatedComponents }))
    },
    [dispatch, allComponents, selectedIds]
  )

  return { onAlign }
}
